// Accounting hub — outlet panel with four tabs:
//   Expenses | Ageing | Settlements | Reports
//
// Also hosts the admin "Payment accounts" settings (pay-by -> Chart of
// Accounts mapping) because that mapping drives every accounting report.

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type { AccountingMeta } from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';
import './expense-manage.js';
import './aging-panel.js';
import './settlements-panel.js';
import './accounting-reports.js';

type AccountingTab = 'expenses' | 'aging' | 'settlements' | 'reports';

interface KeptAccountingView {
  tab: AccountingTab;
}

interface MappingRow {
  id: string;
  name: string;
  kind: string;
  accountEntityId: number;
  saving: boolean;
  error: string;
}

const KINDS = [
  { id: 'cash', label: 'Cash' },
  { id: 'bank', label: 'Bank' },
  { id: 'card', label: 'Card clearing' },
  { id: 'clearing_online', label: 'Online provider clearing' },
  { id: 'receivable', label: 'Receivable (per party)' },
  { id: 'other', label: 'Other' },
] as const;

@customElement('accounting-page')
export class AccountingPage extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .head { display: flex; align-items: center; gap: 0.5rem; padding: 0.6rem 1rem 0; flex-shrink: 0; }
    .head h2 { margin: 0; font-size: 1.02rem; }
    .head .spacer { flex: 1; }
    .tabs { margin: 0.55rem 1rem 0; margin-bottom: 0; }
    .pane { flex: 1; min-height: 0; display: flex; flex-direction: column; }
    .pane > * { flex: 1; min-height: 0; }
    .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); display: none; align-items: center; justify-content: center; z-index: 60; padding: 1rem; }
    .modal-overlay.open { display: flex; }
    .modal { width: min(760px, 100%); max-height: 90vh; overflow: auto; background: var(--color-surface); border-radius: 16px; }
    .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.1rem; border-bottom: 1px solid var(--color-border); }
    .modal-header h3 { margin: 0; font-size: 1rem; }
    .modal-body { padding: 1rem 1.1rem; }
    .modal-footer { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 0.8rem 1.1rem; border-top: 1px solid var(--color-border); }
    .close { border: none; background: none; font-size: 1.35rem; cursor: pointer; color: var(--color-text-secondary); }
    .hint { font-size: 0.76rem; color: var(--color-text-secondary); margin-bottom: 0.7rem; }
    .err { color: var(--color-error, #d93025); font-size: 0.76rem; }
    .wrap { border: 1px solid var(--color-border); border-radius: 12px; overflow: auto; }
    select { min-height: 36px; padding: 6px 9px; border: 1px solid var(--color-border); border-radius: 8px; background: var(--color-surface); color: var(--color-text); font-family: inherit; font-size: 0.82rem; }
    .state { padding: 2rem; text-align: center; color: var(--color-text-secondary); }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: Boolean }) isAdmin = false;

  @state() private _tab: AccountingTab = 'expenses';
  @state() private _settingsOpen = false;
  @state() private _mapping: MappingRow[] = [];
  @state() private _accounts: Array<{ id: string; name: string }> = [];
  @state() private _settingsLoading = false;
  @state() private _settingsError = '';

  private _view = new OutletViewCache<KeptAccountingView>(this, 'accounting-page', {
    capture: () => ({ tab: this._tab }),
    restore: (v) => { this._tab = v.tab; this._settingsOpen = false; },
    reset: () => { this._tab = 'expenses'; this._settingsOpen = false; },
  });

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._view.dispose();
  }

  protected updated() {
    this._view.switchTo(this.storeCode);
  }

  private async _openSettings() {
    this._settingsOpen = true;
    this._settingsLoading = true;
    this._settingsError = '';
    const epoch = this._view.epoch;
    try {
      const res = await api.accountingMeta(this.storeCode);
      if (!this._view.isCurrent(epoch)) return;
      const meta: AccountingMeta = res.data;
      this._accounts = meta.accounts;
      this._mapping = meta.payBy.map((p) => ({
        id: p.id,
        name: p.name,
        kind: p.kind || 'other',
        accountEntityId: p.accountEntityId || 0,
        saving: false,
        error: '',
      }));
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._settingsError = e?.message || 'Failed to load the mapping';
    } finally {
      if (this._view.isCurrent(epoch)) this._settingsLoading = false;
    }
  }

  private _patchRow(id: string, patch: Partial<MappingRow>) {
    this._mapping = this._mapping.map((r) => (r.id === id ? { ...r, ...patch } : r));
  }

  private async _saveRow(row: MappingRow) {
    this._patchRow(row.id, { saving: true, error: '' });
    try {
      await api.accountingSetPayByAccount(this.storeCode, row.id, row.kind, row.accountEntityId);
      this._patchRow(row.id, { saving: false });
    } catch (e: any) {
      this._patchRow(row.id, { saving: false, error: e?.message || 'Save failed' });
    }
  }

  private _tabLabel(id: AccountingTab): string {
    switch (id) {
      case 'expenses': return '💸 Expenses';
      case 'aging': return '📅 Ageing';
      case 'settlements': return '🏦 Settlements';
      default: return '📊 Reports';
    }
  }

  render() {
    if (!this.storeCode) return html`<div class="state">No outlet selected.</div>`;
    const tabs: AccountingTab[] = ['expenses', 'aging', 'settlements', 'reports'];
    return html`
      <div class="head">
        <h2>Accounting</h2>
        <div class="spacer"></div>
        ${this.isAdmin ? html`<button class="btn-sec btn btn-sm" @click=${() => void this._openSettings()}>⚙ Payment accounts</button>` : ''}
      </div>
      <div class="tabs">
        ${tabs.map((t) => html`<button class=${this._tab === t ? 'active' : ''} @click=${() => (this._tab = t)}>${this._tabLabel(t)}</button>`)}
      </div>
      <div class="pane">
        ${this._tab === 'expenses' ? html`<expense-manage .storeCode=${this.storeCode} .isAdmin=${this.isAdmin}></expense-manage>` : ''}
        ${this._tab === 'aging' ? html`<aging-panel .storeCode=${this.storeCode}></aging-panel>` : ''}
        ${this._tab === 'settlements' ? html`<settlements-panel .storeCode=${this.storeCode} .isAdmin=${this.isAdmin}></settlements-panel>` : ''}
        ${this._tab === 'reports' ? html`<accounting-reports .storeCode=${this.storeCode}></accounting-reports>` : ''}
      </div>

      <div class="modal-overlay ${this._settingsOpen ? 'open' : ''}" @click=${(e: Event) => { if (e.target === e.currentTarget) this._settingsOpen = false; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>⚙ Payment accounts</h3>
            <button class="close" @click=${() => (this._settingsOpen = false)}>&times;</button>
          </div>
          <div class="modal-body">
            <div class="hint">
              Each “Paid By” type posts to one Chart-of-Accounts account. Cash / bank accounts show their
              balance directly; card and online providers (ToyyibPay / Billplz) sit in a clearing account
              until you record a payout in the Settlements tab.
            </div>
            ${this._settingsError ? html`<div class="err" style="margin-bottom:0.6rem">${this._settingsError}</div>` : ''}
            ${this._settingsLoading ? html`<div class="state">Loading…</div>` : html`
              <div class="wrap">
                <table>
                  <thead><tr><th>Paid by</th><th>Kind</th><th>Account</th><th></th></tr></thead>
                  <tbody>
                    ${this._mapping.map((r) => html`<tr>
                      <td>${r.name}${r.error ? html`<div class="err">${r.error}</div>` : ''}</td>
                      <td>
                        <select .value=${r.kind} @change=${(e: Event) => this._patchRow(r.id, { kind: (e.target as HTMLSelectElement).value })}>
                          ${KINDS.map((k) => html`<option value=${k.id} ?selected=${r.kind === k.id}>${k.label}</option>`)}
                        </select>
                      </td>
                      <td>
                        <select .value=${String(r.accountEntityId)}
                          ?disabled=${r.kind === 'receivable'}
                          @change=${(e: Event) => this._patchRow(r.id, { accountEntityId: Number((e.target as HTMLSelectElement).value) })}>
                          <option value="0">— none —</option>
                          ${this._accounts.map((a) => html`<option value=${a.id} ?selected=${String(r.accountEntityId) === a.id}>${a.name}</option>`)}
                        </select>
                      </td>
                      <td><button class="btn-sm" ?disabled=${r.saving} @click=${() => void this._saveRow(r)}>${r.saving ? '…' : 'Save'}</button></td>
                    </tr>`)}
                  </tbody>
                </table>
              </div>
            `}
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click=${() => (this._settingsOpen = false)}>Close</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'accounting-page': AccountingPage;
  }
}
